/**
 * Reception i18n — namespaced FR (default) + AR (RTL). Registered once in the module's onRegister.
 * Missing AR key → FR fallback → raw key (i18n.tsx). Interpolation uses {var}.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav + titles
  "nav.reception": "Réception",
  "nav.section.production": "Production",
  "reception.accueil.title": "Accueil",
  "reception.accueil.subtitle": "Intake patient, demandes et prélèvements",
  "reception.intake.title": "Nouveau dossier",
  "reception.demande.workspace.title": "Demande {ref}",

  // search / list
  "reception.search.label": "Rechercher un patient",
  "reception.search.placeholder": "Nom, prénom, téléphone ou NIN…",
  "reception.search.hint": "Saisissez au moins 2 caractères.",
  "reception.search.empty": "Aucun patient ne correspond",
  "reception.search.emptyDesc": "Vérifiez l'orthographe ou créez un nouveau dossier.",
  "reception.list.col.patient": "Patient",
  "reception.list.col.naissance": "Naissance",
  "reception.list.col.telephone": "Téléphone",
  "reception.list.col.nin": "NIN",
  "reception.list.col.actions": "Actions",
  "reception.action.newDossier": "Nouveau dossier",
  "reception.action.open": "Ouvrir",
  "reception.action.newDemande": "Nouvelle demande",
  "reception.action.invite": "Inviter (lien)",

  // dedup
  "reception.dedup.title": "Dossiers existants",
  "reception.dedup.match": "{n} dossier(s) déjà au laboratoire",
  "reception.dedup.useExisting": "Utiliser ce dossier",
  "reception.dedup.continueNew": "Créer quand même un nouveau dossier",
  "reception.dedup.ninChecking": "Vérification du NIN…",
  "reception.dedup.ninCentralOff": "Suggestion inter-laboratoires indisponible (EB-03).",
  "reception.dedup.ninCentralMatch": "Correspondance centrale trouvée — à confirmer.",

  // identity form (D-13 mandatory)
  "reception.field.nom": "Nom",
  "reception.field.prenom": "Prénom",
  "reception.field.sexe": "Sexe",
  "reception.field.sexe.m": "Masculin",
  "reception.field.sexe.f": "Féminin",
  "reception.field.sexe.i": "Indéterminé",
  "reception.field.dateNaissance": "Date de naissance",
  "reception.field.telephone": "Téléphone",
  "reception.field.nin": "NIN",
  "reception.field.wilaya": "Wilaya",
  "reception.field.commune": "Commune",
  "reception.field.adresse": "Adresse",
  "reception.field.groupeSanguin": "Groupe sanguin",
  "reception.field.antecedents": "Antécédents",
  "reception.field.traitements": "Traitements",
  "reception.field.allergies": "Allergies",
  "reception.section.identite": "Identité (obligatoire)",
  "reception.section.dossier": "Dossier clinique (optionnel)",
  "reception.form.required": "Champ obligatoire.",
  "reception.form.save": "Enregistrer le dossier",
  "reception.form.cancel": "Annuler",
  "reception.toast.dossierCreated": "Dossier créé.",
  "reception.toast.dossierError": "Échec de la création du dossier.",

  // demande / basket
  "reception.demande.title": "Demande d'analyses",
  "reception.demande.tab.basket": "Panier",
  "reception.demande.tab.billing": "Facturation",
  "reception.demande.tab.tubes": "Tubes & prélèvement",
  "reception.demande.priorite": "Priorité",
  "reception.demande.priorite.routine": "Routine",
  "reception.demande.priorite.urgent": "Urgent",
  "reception.demande.addParam": "Ajouter un paramètre",
  "reception.demande.addParamHint": "Recherchez par code, libellé ou discipline (catalogue tarifé du labo).",
  "reception.demande.addParamPlaceholder": "Code, libellé ou discipline…",
  // shared catalogue picker (NOVA-011/032)
  "catalogue.picker.searching": "Recherche…",
  "catalogue.picker.empty": "Aucun paramètre ne correspond",
  "catalogue.picker.error": "Échec de la recherche du catalogue",
  "reception.demande.basket.empty": "Panier vide",
  "reception.demande.basket.emptyDesc": "Ajoutez un paramètre par recherche au catalogue ou par scan.",
  "reception.demande.col.code": "Code",
  "reception.demande.col.libelle": "Libellé",
  "reception.demande.col.prix": "Prix",
  "reception.demande.create": "Créer la demande",
  "reception.demande.total": "Total",
  "reception.toast.demandeCreated": "Demande {ref} créée.",
  "reception.toast.demandeError": "Échec de la création de la demande.",
  "reception.toast.demandeCancelled": "Demande annulée.",
  "reception.demande.cancel": "Annuler la demande",
  "reception.demande.cancelConfirm": "Annuler définitivement cette demande ?",
  "reception.demande.statut": "Statut",

  // scan
  "reception.scan.title": "Scanner une ordonnance",
  "reception.scan.label": "QR / code de l'ordonnance",
  "reception.scan.placeholder": "Collez ou scannez le code…",
  "reception.scan.submit": "Résoudre l'ordonnance",
  "reception.scan.unavailable": "Scan indisponible — utilisez la saisie manuelle.",
  "reception.scan.matched": "Lignes reconnues",
  "reception.scan.unmatched": "Non mappées (jamais ajoutées automatiquement)",
  "reception.scan.seed": "Ajouter les lignes reconnues au panier",

  // billing
  "reception.billing.title": "Facturation",
  "reception.billing.issue": "Émettre la facture",
  "reception.billing.issued": "Facture émise.",
  "reception.billing.remise": "Remise",
  "reception.billing.facture": "Facture",
  "reception.billing.needIssue": "Émettez la facture avant le paiement.",
  "reception.billing.pay": "Encaisser",
  "reception.billing.montant": "Montant dû",
  "reception.billing.montantTendu": "Montant remis",
  "reception.billing.rendu": "Monnaie à rendre",
  "reception.billing.paid": "Paiement enregistré.",
  "reception.billing.payError": "Échec du paiement.",
  "reception.billing.caisseClosed": "Aucune caisse ouverte — ouvrez une session de caisse.",

  // tubes
  "reception.tubes.title": "Tubes & prélèvement",
  "reception.tubes.generate": "Générer les tubes",
  "reception.tubes.generated": "{n} tube(s) généré(s).",
  "reception.tubes.needOpen": "La demande doit être ouverte (facturée) pour générer les tubes.",
  "reception.tubes.col.matricule": "Matricule",
  "reception.tubes.col.type": "Type tube",
  "reception.tubes.col.disciplines": "Disciplines",
  "reception.tubes.print": "Imprimer les vignettes",
  "reception.tubes.enqueue": "Envoyer au prélèvement",
  "reception.tubes.enqueued": "Patient envoyé au prélèvement (ticket {n}).",
  "reception.tubes.empty": "Aucun tube",
  "reception.tubes.emptyDesc": "Générez les tubes à partir des lignes de la demande.",

  // lien
  "reception.lien.title": "Lien de connexion patient",
  "reception.lien.canal": "Canal",
  "reception.lien.canal.ailaji": "Ailaji",
  "reception.lien.canal.portail": "Portail",
  "reception.lien.mint": "Générer l'invitation",
  "reception.lien.minted": "Invitation générée.",
  "reception.lien.code": "Code d'invitation (à remettre une seule fois)",
  "reception.lien.copy": "Copier",
  "reception.lien.copied": "Code copié.",
  "reception.lien.warn": "Ce code n'est affiché qu'une seule fois — remettez-le au patient.",

  // generic
  "reception.error.generic": "Une erreur est survenue.",
  "reception.back": "Retour à l'accueil",
};

const AR: Record<string, string> = {
  "nav.reception": "الاستقبال",
  "nav.section.production": "الإنتاج",
  "reception.accueil.title": "الاستقبال",
  "reception.accueil.subtitle": "تسجيل المريض، الطلبات والعينات",
  "reception.intake.title": "ملف جديد",
  "reception.demande.workspace.title": "الطلب {ref}",

  "reception.search.label": "البحث عن مريض",
  "reception.search.placeholder": "الاسم، اللقب، الهاتف أو رقم التعريف…",
  "reception.search.hint": "أدخل حرفين على الأقل.",
  "reception.search.empty": "لا يوجد مريض مطابق",
  "reception.search.emptyDesc": "تحقق من الكتابة أو أنشئ ملفًا جديدًا.",
  "reception.list.col.patient": "المريض",
  "reception.list.col.naissance": "تاريخ الميلاد",
  "reception.list.col.telephone": "الهاتف",
  "reception.list.col.nin": "رقم التعريف",
  "reception.list.col.actions": "إجراءات",
  "reception.action.newDossier": "ملف جديد",
  "reception.action.open": "فتح",
  "reception.action.newDemande": "طلب جديد",
  "reception.action.invite": "دعوة (رابط)",

  "reception.dedup.title": "ملفات موجودة",
  "reception.dedup.match": "{n} ملف موجود مسبقًا في المخبر",
  "reception.dedup.useExisting": "استخدام هذا الملف",
  "reception.dedup.continueNew": "إنشاء ملف جديد رغم ذلك",
  "reception.dedup.ninChecking": "جارٍ التحقق من رقم التعريف…",
  "reception.dedup.ninCentralOff": "اقتراح بين المخابر غير متاح (EB-03).",
  "reception.dedup.ninCentralMatch": "تم العثور على تطابق مركزي — يحتاج تأكيدًا.",

  "reception.field.nom": "اللقب",
  "reception.field.prenom": "الاسم",
  "reception.field.sexe": "الجنس",
  "reception.field.sexe.m": "ذكر",
  "reception.field.sexe.f": "أنثى",
  "reception.field.sexe.i": "غير محدد",
  "reception.field.dateNaissance": "تاريخ الميلاد",
  "reception.field.telephone": "الهاتف",
  "reception.field.nin": "رقم التعريف الوطني",
  "reception.field.wilaya": "الولاية",
  "reception.field.commune": "البلدية",
  "reception.field.adresse": "العنوان",
  "reception.field.groupeSanguin": "الزمرة الدموية",
  "reception.field.antecedents": "السوابق المرضية",
  "reception.field.traitements": "العلاجات",
  "reception.field.allergies": "الحساسية",
  "reception.section.identite": "الهوية (إلزامي)",
  "reception.section.dossier": "الملف السريري (اختياري)",
  "reception.form.required": "حقل إلزامي.",
  "reception.form.save": "حفظ الملف",
  "reception.form.cancel": "إلغاء",
  "reception.toast.dossierCreated": "تم إنشاء الملف.",
  "reception.toast.dossierError": "فشل إنشاء الملف.",

  "reception.demande.title": "طلب تحاليل",
  "reception.demande.tab.basket": "السلة",
  "reception.demande.tab.billing": "الفوترة",
  "reception.demande.tab.tubes": "الأنابيب والعينات",
  "reception.demande.priorite": "الأولوية",
  "reception.demande.priorite.routine": "عادي",
  "reception.demande.priorite.urgent": "مستعجل",
  "reception.demande.addParam": "إضافة معيار",
  "reception.demande.addParamHint": "ابحث بالرمز أو التسمية أو التخصص (كتالوج المخبر المُسعّر).",
  "reception.demande.addParamPlaceholder": "الرمز أو التسمية أو التخصص…",
  // shared catalogue picker (NOVA-011/032)
  "catalogue.picker.searching": "جارٍ البحث…",
  "catalogue.picker.empty": "لا يوجد معيار مطابق",
  "catalogue.picker.error": "فشل البحث في الكتالوج",
  "reception.demande.basket.empty": "السلة فارغة",
  "reception.demande.basket.emptyDesc": "أضف معيارًا عبر البحث في الكتالوج أو بالمسح.",
  "reception.demande.col.code": "الرمز",
  "reception.demande.col.libelle": "التسمية",
  "reception.demande.col.prix": "السعر",
  "reception.demande.create": "إنشاء الطلب",
  "reception.demande.total": "المجموع",
  "reception.toast.demandeCreated": "تم إنشاء الطلب {ref}.",
  "reception.toast.demandeError": "فشل إنشاء الطلب.",
  "reception.toast.demandeCancelled": "تم إلغاء الطلب.",
  "reception.demande.cancel": "إلغاء الطلب",
  "reception.demande.cancelConfirm": "إلغاء هذا الطلب نهائيًا؟",
  "reception.demande.statut": "الحالة",

  "reception.scan.title": "مسح وصفة طبية",
  "reception.scan.label": "رمز QR / رمز الوصفة",
  "reception.scan.placeholder": "ألصق أو امسح الرمز…",
  "reception.scan.submit": "تحليل الوصفة",
  "reception.scan.unavailable": "المسح غير متاح — استخدم الإدخال اليدوي.",
  "reception.scan.matched": "أسطر معروفة",
  "reception.scan.unmatched": "غير مطابقة (لا تُضاف تلقائيًا)",
  "reception.scan.seed": "إضافة الأسطر المعروفة إلى السلة",

  "reception.billing.title": "الفوترة",
  "reception.billing.issue": "إصدار الفاتورة",
  "reception.billing.issued": "تم إصدار الفاتورة.",
  "reception.billing.remise": "تخفيض",
  "reception.billing.facture": "الفاتورة",
  "reception.billing.needIssue": "أصدر الفاتورة قبل الدفع.",
  "reception.billing.pay": "تحصيل",
  "reception.billing.montant": "المبلغ المستحق",
  "reception.billing.montantTendu": "المبلغ المدفوع",
  "reception.billing.rendu": "الباقي المسترجع",
  "reception.billing.paid": "تم تسجيل الدفع.",
  "reception.billing.payError": "فشل الدفع.",
  "reception.billing.caisseClosed": "لا توجد صندوق مفتوح — افتح جلسة صندوق.",

  "reception.tubes.title": "الأنابيب والعينات",
  "reception.tubes.generate": "توليد الأنابيب",
  "reception.tubes.generated": "تم توليد {n} أنبوب.",
  "reception.tubes.needOpen": "يجب أن يكون الطلب مفتوحًا (مفوترًا) لتوليد الأنابيب.",
  "reception.tubes.col.matricule": "المعرّف",
  "reception.tubes.col.type": "نوع الأنبوب",
  "reception.tubes.col.disciplines": "التخصصات",
  "reception.tubes.print": "طباعة الملصقات",
  "reception.tubes.enqueue": "إرسال إلى أخذ العينة",
  "reception.tubes.enqueued": "تم إرسال المريض إلى أخذ العينة (التذكرة {n}).",
  "reception.tubes.empty": "لا توجد أنابيب",
  "reception.tubes.emptyDesc": "ولّد الأنابيب من أسطر الطلب.",

  "reception.lien.title": "رابط ربط المريض",
  "reception.lien.canal": "القناة",
  "reception.lien.canal.ailaji": "أيلاجي",
  "reception.lien.canal.portail": "البوابة",
  "reception.lien.mint": "توليد الدعوة",
  "reception.lien.minted": "تم توليد الدعوة.",
  "reception.lien.code": "رمز الدعوة (يُسلّم مرة واحدة فقط)",
  "reception.lien.copy": "نسخ",
  "reception.lien.copied": "تم نسخ الرمز.",
  "reception.lien.warn": "يُعرض هذا الرمز مرة واحدة فقط — سلّمه للمريض.",

  "reception.error.generic": "حدث خطأ.",
  "reception.back": "العودة إلى الاستقبال",
};

export function registerReceptionMessages(): void {
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
